/**
 * Generic online-rooms client plumbing (any game on the gateway room layer,
 * general_services/online_rooms.py): room HTTP API, the room WebSocket with
 * reconnect, per-game seat storage, invite links and a snapshot buffer.
 * The server is authoritative; a client only sends its own seat's input.
 */
import { API_BASE_URL } from '../../../utils/config';

const BUFFER_MAX = 30;

export function gatewayHttpBase() {
  return String(API_BASE_URL || '/gateway').replace(/\/$/, '');
}

/** ws(s):// URL for a gateway path, same origin unless API_BASE_URL is absolute. */
export function gatewayWsUrl(path, loc = (typeof window !== 'undefined' ? window.location : null)) {
  const base = gatewayHttpBase();
  if (/^https?:\/\//i.test(base)) {
    return base.replace(/^http/i, 'ws') + path;
  }
  const secure = loc && loc.protocol === 'https:';
  const host = loc ? loc.host : 'localhost';
  return `${secure ? 'wss' : 'ws'}://${host}${base}${path}`;
}

export function authHeaders() {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

async function call(method, path, body) {
  const res = await fetch(`${gatewayHttpBase()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    const err = new Error((data && (data.error || data.code)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data && data.code;
    throw err;
  }
  return data;
}

/** Room API for one game id ('tjp', 'si', …): /api/<game>/rooms*. */
export function roomsApi(game) {
  const root = `/api/${game}/rooms`;
  const one = (id) => `${root}/${encodeURIComponent(id)}`;
  return {
    list: () => call('GET', root).then((d) => (d && d.rooms) || []),
    create: (opts = {}) => call('POST', root, opts),
    match: (opts = {}) => call('POST', `${root}/match`, opts),
    join: (roomId) => call('POST', `${one(roomId)}/join`, {}),
    info: (roomId) => call('GET', one(roomId)),
    leave: (roomId, seatToken) => call('POST', `${one(roomId)}/leave`, { seat_token: seatToken }),
  };
}

/** Fire-and-forget leave that survives page unload. */
export function beaconLeaveRoom(game, roomId, seatToken) {
  const url = `${gatewayHttpBase()}/api/${game}/rooms/${encodeURIComponent(roomId)}/leave`;
  const body = JSON.stringify({ seat_token: seatToken });
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
  } catch (_) { /* fall through */ }
  try { fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }); } catch (_) { /* ignore */ }
}

/** Seat token survives a reload (same tab, per game) so the seat can reconnect. */
export function seatStore(game) {
  const key = `evlx_${game}_online_seat`;
  return {
    save(grant) {
      try {
        sessionStorage.setItem(key, JSON.stringify({
          room_id: grant.room_id, seat: grant.seat, seat_token: grant.seat_token, ws_path: grant.ws_path,
        }));
      } catch (_) { /* ignore */ }
    },
    load() {
      try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; }
    },
    clear() {
      try { sessionStorage.removeItem(key); } catch (_) { /* ignore */ }
    },
  };
}

export function roomParamFromSearch(search, param) {
  const re = new RegExp(`[?&]${param}=([A-Za-z0-9_-]{1,64})`);
  const m = re.exec(search || '');
  return m ? m[1] : null;
}

export function roomInviteLink(roomId, param, loc = window.location) {
  const url = new URL(loc.href);
  url.searchParams.set(param, roomId);
  return url.toString();
}

/** Drop ?<param>= from the address bar (no reload) so a reload does not re-join. */
export function clearRoomParam(param, win = (typeof window !== 'undefined' ? window : null)) {
  if (!win || !win.history || !win.location) return;
  try {
    const url = new URL(win.location.href);
    if (!url.searchParams.has(param)) return;
    url.searchParams.delete(param);
    win.history.replaceState(win.history.state, '', url.toString());
  } catch (_) { /* ignore */ }
}

/**
 * Invite link: look at the room before joining. Resolves to
 *   { join: true }                          open lobby (or state unknown) → join
 *   { join: false, message, result? }       finished / started / gone → show message
 * Never rejects; an unreachable info call falls back to joining.
 */
export async function inviteRoomStatus(api, roomId) {
  let info;
  try {
    info = await Promise.resolve(api.info(roomId));
  } catch (e) {
    if (e && e.status === 404) return { join: false, message: 'That room no longer exists.' };
    return { join: true };
  }
  if (!info || !info.phase || info.phase === 'waiting') return { join: true };
  if (info.phase === 'finished') {
    return { join: false, message: 'That match has finished.', result: info.result || null };
  }
  return { join: false, message: 'That match has already started.' };
}

/** Friendly text for a failed create/join/match call. */
export function roomErrorMessage(e) {
  const why = e && e.message ? e.message : 'failed';
  if (/not_joinable:finished|room_finished/.test(why)) return 'That match has finished.';
  if (/not_joinable|lobby_full|room_full|seat_taken/.test(why) || (e && e.status === 409)) {
    return 'That room is already full or the match has started.';
  }
  if (/room_not_found/.test(why) || (e && e.status === 404)) return 'No room with that code.';
  if (/too_many/.test(why)) return 'Too many open rooms right now — try again in a minute.';
  return `Could not reach the game server (${why}).`;
}

// ---- snapshot buffer (sampling/lerp is game-specific)
export function createSnapshotBuffer() {
  return { items: [] };
}

export function pushSnapshot(buf, snap, receivedAt) {
  const items = buf.items;
  if (items.length && snap.tick <= items[items.length - 1].snap.tick) return buf;
  items.push({ t: receivedAt, snap });
  if (items.length > BUFFER_MAX) items.splice(0, items.length - BUFFER_MAX);
  return buf;
}

/**
 * Sample the buffer at renderAt with lerp(a, b, f) between the two snapshots
 * around it. Holds the oldest/newest at the ends (no extrapolation).
 */
export function sampleBuffer(buf, renderAt, lerp) {
  const items = buf.items;
  if (!items.length) return null;
  if (renderAt <= items[0].t) return lerp(items[0].snap, items[0].snap, 1);
  for (let i = items.length - 1; i > 0; i -= 1) {
    const a = items[i - 1];
    const b = items[i];
    if (renderAt >= a.t && renderAt <= b.t) {
      const span = b.t - a.t;
      return lerp(a.snap, b.snap, span > 0 ? (renderAt - a.t) / span : 1);
    }
  }
  const last = items[items.length - 1].snap;
  return lerp(last, last, 1);
}

/**
 * Room socket with auto-reconnect inside the server's grace window.
 * Callbacks: onMessage(msg), onStatus('connecting'|'open'|'reconnecting'|'closed', info).
 */
export class RoomSocket {
  constructor(grant, {
    onMessage, onStatus, WebSocketImpl, graceMs = 20000, game,
  } = {}) {
    this.grant = grant;
    this.game = game || grant.game || 'tjp';
    this.onMessage = onMessage || (() => {});
    this.onStatus = onStatus || (() => {});
    this.WS = WebSocketImpl || (typeof WebSocket !== 'undefined' ? WebSocket : null);
    this.graceMs = graceMs;
    this.ws = null;
    this.seq = 0;
    this.closedByUser = false;
    this.droppedAt = null;
    this.retry = 0;
    this.timer = null;
  }

  url() {
    return gatewayWsUrl(this.grant.ws_path || `/ws/${this.game}/${this.grant.room_id}`);
  }

  connect() {
    if (!this.WS) throw new Error('WebSocket unavailable');
    this.onStatus(this.droppedAt ? 'reconnecting' : 'connecting');
    const ws = new this.WS(this.url());
    this.ws = ws;
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'hello', seat_token: this.grant.seat_token }));
    };
    ws.onmessage = (ev) => {
      let msg = null;
      try { msg = JSON.parse(ev.data); } catch (_) { return; }
      if (!msg || msg.type === 'keepalive') return; // server heartbeat for idle lobbies
      if (msg.type === 'welcome') {
        this.droppedAt = null;
        this.retry = 0;
        this.onStatus('open', msg);
      }
      if (msg.type === 'error' && ['bad_seat_token', 'room_finished', 'room_not_found', 'seat_retired'].includes(msg.code)) {
        this.closedByUser = true; // nothing to reconnect to
      }
      if (msg.type === 'kicked') this.closedByUser = true;
      this.onMessage(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closedByUser) {
        this.onStatus('closed');
        return;
      }
      if (!this.droppedAt) this.droppedAt = Date.now();
      if (Date.now() - this.droppedAt > this.graceMs) {
        this.onStatus('closed', { reason: 'grace_expired' });
        return;
      }
      const delay = Math.min(4000, 250 * 2 ** this.retry);
      this.retry += 1;
      this.onStatus('reconnecting', { delay });
      this.timer = setTimeout(() => this.connect(), delay);
    };
  }

  send(msg) {
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  sendAction(action, opts) {
    this.seq += 1;
    const msg = { type: 'input', action, seq: this.seq };
    if (opts && opts.repeat) msg.repeat = true; // held-key auto-repeat (AQ: cleared by stay)
    return this.send(msg);
  }

  close() {
    this.closedByUser = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.ws) {
      try { this.ws.close(); } catch (_) { /* ignore */ }
    }
    this.ws = null;
  }
}
